import {ChangeDetectorRef, ElementRef, ViewContainerRef} from '@angular/core';
import {DialogCloseResult, DialogService} from '@progress/kendo-angular-dialog';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';
import {AuthService} from '../../auth/auth.service';
import {MarketConfigChange} from '../shared/models/markets-config.model';
import {RenameMarketDialogComponent} from './rename-market-dialog/rename-market-dialog.component';
import {SaveChangesDialogComponent} from './save-changes-dialog/save-changes-dialog.component';

// itemKey lets a later edit of the same not-yet-saved item (a block or a service) find and
// update its own pending entry instead of piling up duplicates - stripped before the entry is
// sent to the server.
export type TrackedChange = MarketConfigChange & {itemKey?: string};

// Just enough shape for the market-level bookkeeping below to work without knowing whether a
// market's own items are called "blocks" or "services".
export interface EditableMarketLike {
    key: string;
    name: string;
    isNew: boolean;
}

// Shared by RangesMarketsComponent and ServiceMarketsComponent: market-level add/rename/delete
// and the pending-change bookkeeping for them, plus the dialog plumbing both need - none of it
// ever touches a market's own item list, only {key, name, isNew} and the pending-changes array.
// Mirrors the constructor-injection pattern already used by BaseRuleGrid (legacy-business-
// rules/shared/base-rule-grid.ts) for the same reason: a subclass is a @Component with its own
// constructor, which calls super(...) with the same injected instances plus whatever else it
// needs (e.g. ConfigurationService).
export abstract class BaseMarketEditor {
    public expandedMarkets = new Set<string>();
    public editingKey: string | null = null;
    public saving = false;

    protected pendingChanges: TrackedChange[] = [];
    private nextKeyCounter = 1;

    protected constructor(protected dialogService: DialogService,
                           protected viewContainerRef: ViewContainerRef,
                           protected elementRef: ElementRef,
                           protected cdr: ChangeDetectorRef,
                           protected authService: AuthService,
                           protected notificationPopupSvc: NotificationPopupService) {
    }

    public get isDirty(): boolean {
        return this.pendingChanges.length > 0;
    }

    protected newKey(): string {
        return `k${this.nextKeyCounter++}`;
    }

    protected resetPendingChanges(): void {
        this.pendingChanges = [];
    }

    // ---- Market create/rename/delete ------------------------------------------------------

    // Opens the shared name-prompt dialog (also used for "Add Market", with currentName '').
    // Resolves the confirmed, trimmed name, or null if the user cancelled.
    protected promptForMarketName(title: string, confirmLabel: string, currentName: string,
                                   existingNames: string[]): Observable<string | null> {
        const dialogRef = this.dialogService.open({
            title, content: RenameMarketDialogComponent, appendTo: this.viewContainerRef, width: 480
        });
        const instance = dialogRef.content.instance as RenameMarketDialogComponent;
        instance.currentName = currentName;
        instance.existingNames = existingNames;
        instance.confirmLabel = confirmLabel;
        return dialogRef.result.pipe(
            map((result: any) => (result instanceof DialogCloseResult || !result.confirmed) ? null : result.newName)
        );
    }

    // Renames a market in place and rewrites every pending entry recorded under its old name -
    // the change still happened, it just now happened to the market's current name. Call this,
    // then update rowClass/rebuildRows() as needed - it does not touch rendering itself.
    protected renameMarketChange(market: EditableMarketLike, newName: string): void {
        const oldName = market.name;
        if (newName === oldName) {
            return;
        }

        this.pendingChanges.forEach(change => {
            if (change.market === oldName) {
                change.market = newName;
                if (change.action === 'market_create') {
                    change.after = {name: newName};
                }
            }
        });
        // A market that never existed under the old name has nothing further to log - the
        // market_create entry above already reflects the final name.
        if (!market.isNew) {
            this.pendingChanges.push({action: 'market_rename', market: newName, before: {name: oldName}, after: {name: newName}});
        }

        if (this.expandedMarkets.delete(oldName)) {
            this.expandedMarkets.add(newName);
        }
        market.name = newName;
    }

    protected pushMarketCreateChange(market: EditableMarketLike): void {
        this.pendingChanges.push({action: 'market_create', market: market.name, before: null, after: {name: market.name}});
    }

    // itemsBefore is the market's own items, already snapshotted by the caller (in whatever
    // shape its own audit "before" values use) - kept generic so this never needs to know the
    // field is called .blocks or .services.
    protected deleteMarketChange(market: EditableMarketLike, itemsBefore: any[]): void {
        this.pendingChanges = this.pendingChanges.filter(change => change.market !== market.name);
        if (!market.isNew) {
            this.pendingChanges.push({
                action: 'market_delete',
                market: market.name,
                before: {name: market.name, items: itemsBefore},
                after: null
            });
        }
        this.expandedMarkets.delete(market.name);
    }

    // ---- Dialog plumbing --------------------------------------------------------------------

    // Every caller of confirm() (deleteMarket/deleteBlock/deleteService, both modes) asks about
    // a delete - "Yes" is styled destructive-red via the global .market-confirm-dialog rule
    // (styles.scss) rather than the usual primary blue. This dialog has no content component of
    // its own to attach scoped styles to (Kendo's actions: config renders bare buttons directly
    // into its own template), so it's tagged by adding a class straight to the dialog's host
    // element instead.
    protected confirm(message: string): Observable<boolean> {
        const dialogRef = this.dialogService.open({
            title: 'Confirm',
            content: message,
            appendTo: this.viewContainerRef,
            actions: [{text: 'No'}, {text: 'Yes', primary: true}],
            actionsLayout: 'normal'
        });
        dialogRef.dialog.location.nativeElement.classList.add('market-confirm-dialog');
        return dialogRef.result.pipe(map(result => !(result instanceof DialogCloseResult || (result as any).text === 'No')));
    }

    protected openSaveDialog(mode: 'ranges' | 'services'): Observable<{reason: string | null} | null> {
        const dialogRef = this.dialogService.open({
            title: 'Save market changes',
            content: SaveChangesDialogComponent,
            appendTo: this.viewContainerRef,
            width: 520
        });
        const instance = dialogRef.content.instance as SaveChangesDialogComponent;
        instance.changes = this.pendingChanges;
        instance.email = this.authService.getEmail();
        instance.mode = mode;
        return dialogRef.result.pipe(
            map((result: any) => (result instanceof DialogCloseResult || !result.confirmed) ? null : {reason: result.reason})
        );
    }

    // The grid's own scroll container doesn't follow a newly-added or newly-opened row on its
    // own - with several markets already in the list, "Add Market"/"Edit" added a row below the
    // fold with no visible sign anything happened. setTimeout defers to after this tick's DOM
    // update (detectChanges only updates bindings synchronously; the browser still needs a
    // moment to actually paint the new <tr>) so the row exists to scroll to.
    protected scrollToEditingRow(): void {
        setTimeout(() => {
            const row: HTMLElement = this.elementRef.nativeElement.querySelector('.market-edit-row');
            row?.scrollIntoView({block: 'center', behavior: 'smooth'});
        });
    }

    protected notifySuccess(message: string): void {
        this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {notificationType: 'success', message});
    }

    protected notifyError(message: string): void {
        this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {notificationType: 'error', message});
    }

    // Export is a pure client-side operation - the current on-screen state (unsaved edits
    // included), no API round-trip - so triggering the download is just a Blob + a synthetic
    // link click, shared by both modes rather than duplicated per component.
    protected downloadCsv(content: string, filename: string): void {
        const blob = new Blob([content], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }
}
